import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import APIServicesV2 from '@/api/APIServicesV2'
import { translateVehicleType } from '@/utils/translate'
import { getErrorMessage } from '@/utils/errorHandlers'
import { FormSearchableSelect } from '@/components/newRedesign/form/FormSearchableSelect'
import { Button } from '@/components/newRedesign/primitives/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/newRedesign/primitives/Dialog'
import { cn } from '@/lib/utils'
import {
  useStorageCompaniesFetcher,
  useTransporterCompaniesFetcher,
  useUsersFetcher,
  useVehiclesFetcher,
  useVehicleFullInfo,
} from '../hooks/useContainerQueries'

interface InsuranceDialogProps {
  isOpen: boolean
  onClose: () => void
  containerId: number
  onSuccess?: () => void
}

interface LogisticsFormData {
  storageCompanyId: string | null
  transporterCompanyId: string | null
  transportedById: string | null
  vehicleId: string | null
}

export function InsuranceDialog({
  isOpen,
  onClose,
  containerId,
  onSuccess,
}: InsuranceDialogProps) {
  const queryClient = useQueryClient()

  // Usar hooks con cache de React Query
  const fetchStorageCompanies = useStorageCompaniesFetcher()
  const fetchTransportCompanies = useTransporterCompaniesFetcher()
  const fetchUsers = useUsersFetcher()
  const fetchVehicles = useVehiclesFetcher()

  const { control, handleSubmit, watch, reset } = useForm<LogisticsFormData>({
    defaultValues: {
      storageCompanyId: null,
      transporterCompanyId: null,
      transportedById: null,
      vehicleId: null,
    },
  })

  // Watch for vehicle selection
  const selectedVehicleId = watch('vehicleId')

  // Get vehicle full info using React Query
  const { data: vehicleInfo } = useVehicleFullInfo(selectedVehicleId)

  // Query for container logistics data
  const { data: containerLogistics, isLoading } = useQuery({
    queryKey: ['container_logistics', containerId],
    queryFn: async () => {
      const response = await APIServicesV2.get(
        `/containers/${containerId}/logistics`
      )
      return response.data
    },
    enabled: isOpen && !!containerId,
    staleTime: 5 * 60 * 1000,
  })

  // Mutation for updating container logistics
  const updateLogisticsMutation = useMutation({
    mutationFn: async (data: LogisticsFormData) => {
      const payload = {
        storageCompanyId: data.storageCompanyId
          ? Number(data.storageCompanyId)
          : null,
        transporterCompanyId: data.transporterCompanyId
          ? Number(data.transporterCompanyId)
          : null,
        transportedById: data.transportedById
          ? Number(data.transportedById)
          : null,
        vehicleId: data.vehicleId ? Number(data.vehicleId) : null,
      }

      const response = await APIServicesV2.patch(
        `/containers/${containerId}/logistics`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      toast.success('Información de aseguramiento actualizada correctamente')
      queryClient.invalidateQueries({
        queryKey: ['container_logistics', containerId],
      })
      queryClient.invalidateQueries({ queryKey: ['container', containerId] })
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      onSuccess?.()
      onClose()
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(
        errorMessage || 'Error al actualizar la información de aseguramiento'
      )
    },
  })

  // Set form values when container logistics are loaded
  useEffect(() => {
    if (containerLogistics && isOpen) {
      reset({
        storageCompanyId: containerLogistics.storageCompany?.id
          ? String(containerLogistics.storageCompany.id)
          : null,
        transporterCompanyId: containerLogistics.transporterCompany?.id
          ? String(containerLogistics.transporterCompany.id)
          : null,
        transportedById: containerLogistics.transportedBy?.id
          ? String(containerLogistics.transportedBy.id)
          : null,
        vehicleId: containerLogistics.vehicle?.id
          ? String(containerLogistics.vehicle.id)
          : null,
      })

      // Set initial vehicle info
      if (containerLogistics.vehicle) {
        // Pre-cache the vehicle info so it appears immediately when selected
        queryClient.setQueryData(
          ['vehicle-full-info', String(containerLogistics.vehicle.id)],
          containerLogistics.vehicle,
          {
            updatedAt: Date.now(),
          }
        )
      }
    }
  }, [containerLogistics, isOpen, reset, queryClient])

  const onSubmit = (data: LogisticsFormData) => {
    updateLogisticsMutation.mutate(data)
  }

  if (!isOpen) return null

  return (
    <Dialog onOpenChange={onClose} open={isOpen}>
      <DialogContent
        className={cn(
          'flex max-h-[800px] min-h-[400px] w-full max-w-[800px]',
          'flex-col gap-0 bg-[#FAFAFA] p-0'
        )}
      >
        <DialogHeader className="border-b border-[#D4D4D4] p-4">
          <DialogTitle className="text-xl font-semibold tracking-tight text-[#262626]">
            Aseguramiento
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-1 flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex-1 overflow-y-auto bg-[#FAFAFA] p-4">
            {isLoading ? (
              <div className="flex h-[364px] items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900" />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Almacenaje */}
                <FormSearchableSelect
                  control={control}
                  emptyMessage="No se encontraron almacenes."
                  fetcher={fetchStorageCompanies}
                  label="Almacenaje"
                  name="storageCompanyId"
                  placeholder="Buscar almacenaje..."
                  searchPlaceholder="Buscar..."
                />

                {/* Transportista */}
                <FormSearchableSelect
                  control={control}
                  emptyMessage="No se encontraron transportistas."
                  fetcher={fetchTransportCompanies}
                  label="Transportista"
                  name="transporterCompanyId"
                  placeholder="Buscar transportista..."
                  searchPlaceholder="Buscar..."
                />

                {/* Conductor y Vehículo */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Transportado por */}
                  <FormSearchableSelect
                    control={control}
                    emptyMessage="No se encontraron conductores."
                    fetcher={fetchUsers}
                    label="Transportado por"
                    name="transportedById"
                    placeholder="Seleccionar conductor..."
                    searchPlaceholder="Buscar..."
                  />

                  {/* Vehículo */}
                  <FormSearchableSelect
                    control={control}
                    emptyMessage="No se encontraron vehículos."
                    fetcher={fetchVehicles}
                    label="Vehículo"
                    name="vehicleId"
                    placeholder="Seleccionar vehículo..."
                    searchPlaceholder="Buscar..."
                  />
                </div>

                {/* Detalles del Vehículo */}
                <div
                  className={cn(
                    'shadow-xs flex flex-col gap-4 rounded-xl border',
                    'border-[#D4D4D4] bg-[#FAFAFA] p-4'
                  )}
                >
                  {/* Card Header */}
                  <div className="flex items-start gap-4">
                    <h4 className="text-base font-medium text-[#262626]">
                      Información del vehículo
                    </h4>
                  </div>

                  {/* List - Vehicle Details */}
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-[#737373]">
                        Identificación:
                      </span>
                      <span className="text-sm text-[#262626]">
                        {vehicleInfo?.identification || '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-[#737373]">
                        Número oficial:
                      </span>
                      <span className="text-sm text-[#262626]">
                        {vehicleInfo?.oficialNumber || '-'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-[#737373]">Tipo:</span>
                      <span className="text-sm text-[#262626]">
                        {vehicleInfo?.type
                          ? translateVehicleType(vehicleInfo.type)
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-[#D4D4D4] p-4">
            <Button
              className={cn(
                'min-w-[86px] bg-[#1E293B] text-[#FAFAFA]',
                'hover:bg-[#1E293B]/90'
              )}
              disabled={updateLogisticsMutation.isPending}
              type="submit"
              variant="default"
            >
              {updateLogisticsMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
